import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { buildPatientIdSearch } from 'app/hooks/useContactRelatedObjects'
import type { MessageJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { THREAD_EVENTS_QUERY } from './graphql'
import {
  agentDisabledDividers,
  anchorThreadEvents,
  appointmentMilestones,
  sortDividerItemsByDate,
  type AppointmentForMilestone,
  type ThreadDividerItem,
} from './threadDividerAnchors'

/**
 * Computes the inline milestone dividers for an SMS thread:
 * - "Agent Disabled" dividers from Thread events (aiEnabled changed to false).
 * - "Appointment booked" dividers for every appointment the contact booked while
 *   the conversation was happening (appointmentMadeDate >= first message),
 *   sourced from the appointments query — source-agnostic (AI, manual, scheduler).
 */
export function useThreadDividers(sortedMessages: MessageJson[], patientIds?: string[]) {
  const activeThreadId = useMemo(() => sortedMessages.find((m) => m.threadId)?.threadId, [sortedMessages])
  // Thread changes flow into the Event stream as field diffs. Match this thread's
  // update events; the agent-disabled divider detects data.aiEnabled.to === "false".
  const threadEventSearch = activeThreadId ? `type:Thread AND typeId:${activeThreadId} AND action:updated` : undefined

  const [threadEventsQuery, reexecute] = useQuery({
    query: THREAD_EVENTS_QUERY,
    variables: {
      search: threadEventSearch,
      sort: { field: 'createdDate', direction: 'ASC' },
      pagination: { limit: 200 },
    },
    pause: !activeThreadId,
    requestPolicy: 'cache-and-network',
  })

  const refetchDividers = useCallback(() => {
    reexecute({ requestPolicy: 'network-only' })
  }, [reexecute])

  const disabledItems = useMemo<ThreadDividerItem[]>(
    () => agentDisabledDividers(threadEventsQuery.data?.events?.hits ?? []),
    [threadEventsQuery.data],
  )

  // Appointment milestones: fetch the contact's appointments by patientId, keep
  // the ones booked at/after the thread started.
  const patientIdSearch = useMemo(() => buildPatientIdSearch(patientIds), [patientIds])
  const firstMessageDate = useMemo(() => sortedMessages.find((m) => !!m.createdDate)?.createdDate, [sortedMessages])

  const [appointments, setAppointments] = useState<AppointmentForMilestone[]>([])

  useEffect(() => {
    // Clear any previous contact's appointments up front so a contact switch (or a
    // failed request) can never leave stale milestones on the new thread.
    setAppointments([])
    if (!patientIdSearch) {
      return
    }
    let cancelled = false
    bg(async () => {
      try {
        const { hits } = await gqlFetchList<AppointmentForMilestone>(
          `appointments`,
          {
            search: patientIdSearch,
            sort: { field: `appointmentMadeDate`, direction: `DESC` },
            pagination: { limit: 1000 },
          },
          `id date time type appointmentMadeDate`,
        )
        if (!cancelled) setAppointments(hits)
      } catch (err) {
        if (!cancelled) {
          setAppointments([])
          logError(`Failed to load appointments for thread milestones`, err)
        }
      }
    })
    return () => {
      cancelled = true
    }
  }, [patientIdSearch])

  const milestoneItems = useMemo(
    () => appointmentMilestones(appointments, firstMessageDate),
    [appointments, firstMessageDate],
  )

  const dividersBeforeMessage = useMemo(
    () => anchorThreadEvents(sortDividerItemsByDate([...disabledItems, ...milestoneItems]), sortedMessages),
    [disabledItems, milestoneItems, sortedMessages],
  )

  return { dividersBeforeMessage, activeThreadId, refetchDividers }
}
